import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { profileAPI } from '../api/api';
import { getMockProfile } from '../api/mockData';
import { testProxyConnection } from '../utils/debug';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Avatar,
  Box,
  Grid,
  Divider,
  Alert,
  Snackbar,
  IconButton,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import { PhotoCamera, Save, Edit as EditIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import axios from 'axios';

const Profile = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    department: '',
    position: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      // Test proxy connection first
      await testProxyConnection();
      
      try {
        // For now, use mock data directly to show user info
        console.log('Using mock data directly...');
        const mockResponse = await getMockProfile();
        const mockData = mockResponse.data;
        
        setFormData(prev => ({
          ...prev,
          name: `${mockData.user_data.email.split('@')[0]} (Admin)`,
          email: mockData.user_data.email,
          phone: mockData.employee_found ? mockData.employee_data.phone : '',
          department: mockData.employee_found ? mockData.employee_data.department : '',
          position: mockData.user_data.role === 'admin' ? 'Yönetici' : 'Kullanıcı'
        }));
        
        setMessage({ 
          text: 'Kullanıcı bilgileri gösteriliyor (Demo mod)', 
          type: 'info' 
        });
        setOpenSnackbar(true);
        return;
        
        // Original backend call (commented out for now)
        /*
        // Try without retries first to avoid multiple 500 errors
        const response = await profileAPI.getProfile(0);
        console.log('Profile API Response:', response.data);
        
        if (response.data && response.data.success) {
          const userData = response.data.user_data || {};
          const employeeData = response.data.employee_data || {};
          
          // Handle case where employee data is not found but user exists
          if (!response.data.employee_found) {
            setFormData(prev => ({
              ...prev,
              name: userData.email ? userData.email.split('@')[0] : 'Kullanıcı',
              email: userData.email || '',
              phone: '',
              department: '',
              position: userData.role === 'admin' ? 'Yönetici' : 'Kullanıcı'
            }));
            
            setMessage({ 
              text: 'Profil bilgileriniz henüz oluşturulmamış. Lütfen bilgilerinizi güncelleyin.', 
              type: 'info' 
            });
            setOpenSnackbar(true);
          } else {
            // Handle case where employee data exists - use correct field names from backend
            setFormData(prev => ({
              ...prev,
              name: employeeData.first_name ? 
                `${employeeData.first_name} ${employeeData.last_name || ''}`.trim() : 
                (userData.email ? userData.email.split('@')[0] : 'Kullanıcı'),
              email: userData.email || employeeData.email || '',
              phone: employeeData.phone || '',
              department: employeeData.department || '',
              position: employeeData.position || (userData.role === 'admin' ? 'Yönetici' : 'Kullanıcı')
            }));
          }
        } else {
          throw new Error(response.data?.message || 'Profil bilgileri alınamadı');
        }
        */
      } catch (error) {
        console.error('Profil yüklenirken hata:', error);
        
        let errorMessage = 'Profil bilgileri yüklenirken bir hata oluştu';
        
        if (error.response?.status === 500) {
          errorMessage = 'Sunucu hatası: Profil servisi şu anda kullanılamıyor. Lütfen daha sonra tekrar deneyin.';
        } else if (error.response?.status === 401) {
          errorMessage = 'Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.';
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message === 'Network Error') {
          errorMessage = 'Bağlantı hatası: Sunucuya ulaşılamıyor.';
        }
        
        setMessage({ text: errorMessage, type: 'error' });
        setOpenSnackbar(true);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    if (formData.newPassword && formData.newPassword !== formData.confirmPassword) {
      setMessage({ text: 'Yeni şifreler eşleşmiyor', type: 'error' });
      setOpenSnackbar(true);
      return;
    }

    try {
      const updateData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        department: formData.department,
        position: formData.position
      };

      // Only include password fields if they are being changed
      if (formData.currentPassword) {
        updateData.currentPassword = formData.currentPassword;
        updateData.newPassword = formData.newPassword;
      }

      const response = await profileAPI.updateProfile(updateData);

      if (response.data.success) {
        setMessage({ text: 'Profil başarıyla güncellendi', type: 'success' });
        setIsEditing(false);
        
        // Clear password fields after successful update
        setFormData(prev => ({
          ...prev,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        }));
      } else {
        setMessage({ 
          text: response.data.message || 'Profil güncellenirken bir hata oluştu', 
          type: 'error' 
        });
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      const errorMessage = error.response?.data?.message || 'Bir hata oluştu. Lütfen tekrar deneyin.';
      setMessage({ text: errorMessage, type: 'error' });
    }
    setOpenSnackbar(true);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">Profilim</Typography>
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => isEditing ? handleSubmit() : setIsEditing(true)}
            startIcon={isEditing ? <Save /> : <EditIcon />}
            type={isEditing ? 'submit' : 'button'}
          >
            {isEditing ? 'Değişiklikleri Kaydet' : 'Profili Düzenle'}
          </Button>
        </Box>
        <Divider sx={{ my: 3 }} />

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box sx={{ position: 'relative', mb: 2 }}>
                <Avatar
                  sx={{ width: 150, height: 150, fontSize: '3rem' }}
                  src={user?.profileImage}
                >
                  {user?.name?.charAt(0) || 'U'}
                </Avatar>
                {isEditing && (
                  <IconButton
                    color="primary"
                    aria-label="upload picture"
                    component="label"
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      backgroundColor: 'white',
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.8)' },
                    }}
                  >
                    <input hidden accept="image/*" type="file" />
                    <PhotoCamera />
                  </IconButton>
                )}
              </Box>
              <Typography variant="h6" sx={{ mt: 1 }}>
                {user?.name || 'Kullanici Adi'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user?.position || 'Pozisyon'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user?.department || 'Departman'}
              </Typography>
            </Grid>

            <Grid item xs={12} md={8}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Ad Soyad"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    disabled={!isEditing}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="E-posta"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={!isEditing}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Telefon"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    disabled={!isEditing}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Departman"
                    name="department"
                    value={formData.department}
                    onChange={handleChange}
                    disabled={!isEditing}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Pozisyon"
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                    disabled={!isEditing}
                    margin="normal"
                  />
                </Grid>

                {isEditing && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }}>
                        <Typography color="text.secondary">Şifre Değiştir</Typography>
                      </Divider>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Mevcut Şifre"
                        name="currentPassword"
                        type={showPassword ? 'text' : 'password'}
                        value={formData.currentPassword}
                        onChange={handleChange}
                        margin="normal"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} />
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Yeni Şifre"
                        name="newPassword"
                        type={showPassword ? 'text' : 'password'}
                        value={formData.newPassword}
                        onChange={handleChange}
                        margin="normal"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Yeni Şifre (Tekrar)"
                        name="confirmPassword"
                        type={showPassword ? 'text' : 'password'}
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        margin="normal"
                        error={formData.newPassword !== formData.confirmPassword && formData.confirmPassword !== ''}
                        helperText={formData.newPassword !== formData.confirmPassword && formData.confirmPassword !== '' ? 'Şifreler eşleşmiyor' : ''}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </>
                )}
              </Grid>

              {isEditing && (
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setIsEditing(false);
                      // Reset form to original values
                      setFormData(prev => ({
                        ...prev,
                        currentPassword: '',
                        newPassword: '',
                        confirmPassword: ''
                      }));
                    }}
                  >
                    İptal
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    startIcon={<Save />}
                  >
                    Kaydet
                  </Button>
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={message.type} 
          sx={{ width: '100%' }}
        >
          {message.text}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Profile;
